import { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Box, Card, CardContent, CardHeader } from "@mui/material"
import { useToast } from "@crm-src/hooks/use-toast"
import { useEmailCampaigns } from "@crm-src/hooks/useEmailCampaigns"
import { Users } from "lucide-react"

import { type EmailCampaign } from "@crm-src/lib/store/campaignsSlice"
import { type TargetSelection } from "@crm-src/components/email-target-selector"
import AudienceBuilder from "@crm-src/components/audience-builder/AudienceBuilder"
import { sanitizeTargetSelection } from "./sanitize-target-selection"
import { type CampaignAttachedFile, uploadLocalFilesToCampaign, savedAttachmentsToFiles } from "@crm-src/components/campaign-file-attachment"
import SendTestEmailDialog from "@crm-src/components/send-test-email-dialog"
import { getLayoutById, type EmailLayoutType, injectTenantInfo, type TenantInfo } from "@crm-src/lib/email-layouts"
import { emailCampaignsPath } from "@crm-src/lib/email-campaign-routes"
import { extractApiErrorMessage } from "@crm-src/lib/api-error"
import { useAuth } from "@crm-src/hooks/useAuth"
import { useAppSelector } from "@crm-src/lib/store"
import FormHeader from "@crm-src/components/email-campaign-form/form-header"
import ContentEditor from "@crm-src/components/email-campaign-form/content-editor"
import CampaignEmailPreview from "@crm-src/components/email-campaign-form/campaign-email-preview"
import SectionNav from "@crm-src/components/email-campaign-form/section-nav"
import { CAMPAIGN_SECTIONS, SECTION_SCROLL_MARGIN_TOP } from "@crm-src/components/email-campaign-form/sections"
import { makeCampaignSaver, type CampaignSaver } from "@crm-src/lib/campaign-draft-saver"
import { DEFAULT_CAMPAIGN_FROM, validateFromAddress, validateReplyTo } from "@crm-src/lib/sender-fields"
import { consumeOttoDraft } from "@crm-src/lib/otto-draft-handoff"
import { validateOttoCampaignDraft } from "@crm-src/lib/otto-campaign-draft"
import {
  linkCampaignToWebinar,
  readWebinarSeed,
  type WebinarCampaignSeedState,
} from "@crm-src/lib/webinar-campaign-link"
import { campaignSendBlockReason } from "@crm-src/components/masthead-newsletter-builder/send-validation"

interface EmailCampaignFormProps {
  existingCampaign?: EmailCampaign | null
}

function EmailCampaignForm({ existingCampaign }: EmailCampaignFormProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const { user } = useAuth()
  const tenant = useAppSelector((state) => state.auth.tenant)
  const {
    createCampaign,
    updateCampaign,
    sendTestEmail,
    deleteCampaign,
  } = useEmailCampaigns()

  const isEditMode = !!existingCampaign

  // Form state for campaign
  const [campaign, setCampaign] = useState({
    subject: "",
    content: "",
    layout: undefined as EmailLayoutType | undefined,
    target_selection: {
      recipients: []
    } as TargetSelection,
    files: [] as CampaignAttachedFile[],
    content_data: null as any,
    // Per-campaign sender. From defaults to the marcom marketing mailbox; a
    // blank From name / Reply-To falls back to the tenant name / the From.
    from_address: DEFAULT_CAMPAIGN_FROM,
    from_name: "",
    reply_to: "",
  })

  const [isSaving, setIsSaving] = useState(false)
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  
  // Auto-save state
  const [draftCampaignId, setDraftCampaignId] = useState<string | null>(null)
  // `draftIdRef` mirrors `draftCampaignId` *synchronously* (React state lags a
  // render, which was the gap the duplicate create slipped through). The
  // in-flight-create lock lives inside the shared saver (makeCampaignSaver).
  const draftIdRef = useRef<string | null>(null)
  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [timeAgo, setTimeAgo] = useState<string>("")
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const renderCountRef = useRef(0)
  const hasUserMadeChangesRef = useRef(false)
  const autoSaveEnabledRef = useRef(false) // Explicitly disabled until user makes a change

  // Seed a new campaign's recipients from the navigation that opened it — used
  // by the Contacts page's "Email selected" / "Email all in {industry}" hand-off
  // (navigate(.../create, { state: { initialTargetSelection } })). Runs once,
  // create-mode only; the user still must add a subject + content to send.
  const seededFromNavRef = useRef(false)
  useEffect(() => {
    if (isEditMode || seededFromNavRef.current) return
    const seed = (location.state as { initialTargetSelection?: TargetSelection } | null)?.initialTargetSelection
    const hasSeed = !!seed && (
      (seed.recipients?.length ?? 0) > 0 ||
      (seed.groups?.length ?? 0) > 0 ||
      (seed.filters?.industries?.length ?? 0) > 0
    )
    if (!hasSeed) return
    seededFromNavRef.current = true
    setCampaign(prev => ({ ...prev, target_selection: seed as TargetSelection }))
    hasUserMadeChangesRef.current = true
  }, [isEditMode, location.state])

  // Use refs to store content/content_data immediately (synchronously) to avoid state update delays
  const contentRef = useRef<string>(campaign.content)
  const contentDataRef = useRef<any>(campaign.content_data)
  const lastAutoSavedContentRef = useRef<string>("")
  const lastAutoSavedSubjectRef = useRef<string>("")
  const lastAutoSavedTargetSelectionRef = useRef<string>("")
  const lastAutoSavedFilesRef = useRef<number>(0)
  const lastAutoSavedSenderRef = useRef<string>("")

  // Otto Agent hand-off: a fully-formed campaign draft stashed under
  // `?draft=<nonce>` (subject + fragment body + layout + recipients). Seeds the
  // create-mode form so the human reviews + sends. Single-use, create-mode only.
  // Placed AFTER `contentRef` so it can sync the synchronous content mirror (an
  // empty mirror would let the first auto-save persist blank content).
  const seededFromOttoRef = useRef(false)
  useEffect(() => {
    if (isEditMode || seededFromOttoRef.current) return
    const nonce = searchParams.get('draft')
    if (!nonce) return
    seededFromOttoRef.current = true
    try {
      const draft = validateOttoCampaignDraft(consumeOttoDraft(nonce))
      setCampaign(prev => ({
        ...prev,
        subject: draft.subject,
        content: draft.content,
        layout: draft.layout,
        target_selection: draft.target_selection,
        ...(draft.from_address ? { from_address: draft.from_address } : {}),
        ...(draft.from_name ? { from_name: draft.from_name } : {}),
      }))
      contentRef.current = draft.content
      hasUserMadeChangesRef.current = true
    } catch (error) {
      console.error('Invalid Otto campaign draft:', error)
      toast({ title: 'Could not open the draft', description: 'Otto’s campaign draft was incomplete.', variant: 'destructive' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, searchParams])

  // Marketing ▸ Webinars hand-off (step 1 "Build the invitation email"). The
  // Webinars page navigates here with the event's details already shaped into
  // this builder's OWN sections, so the operator lands on a nearly-finished
  // webinar invitation instead of the builder's generic placeholder copy.
  //
  // We seed `content_data.sections` + the `webinar` layout and let the
  // WebinarBuilder render the HTML — deliberately NOT generating that HTML on
  // the webinar side, because this email already has two renderers (this
  // builder and crm-api's `WebinarHtmlGeneratorService` fallback) and a third
  // would drift from both. `webinarSeedRef` survives to `onCreated` below,
  // where the new campaign id is reported back to the webinar.
  const webinarSeedRef = useRef<WebinarCampaignSeedState | null>(null)
  const seededFromWebinarRef = useRef(false)
  useEffect(() => {
    if (isEditMode || seededFromWebinarRef.current) return
    const seed = readWebinarSeed(location.state)
    if (!seed) return
    seededFromWebinarRef.current = true
    webinarSeedRef.current = seed
    const contentData = { sections: seed.sections }
    setCampaign(prev => ({
      ...prev,
      subject: seed.subject,
      layout: 'webinar' as EmailLayoutType,
      content_data: contentData,
    }))
    // Mirror into the synchronous ref the auto-save reads, so the first save
    // cannot persist a campaign with the layout but no sections.
    contentDataRef.current = contentData
    hasUserMadeChangesRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, location.state])

  // Track last content/sections from newsletter builder to prevent infinite loops
  const lastNewsletterContentRef = useRef<string>("")
  const lastNewsletterSectionsRef = useRef<string>("")
  
  // Debounce newsletter updates to prevent cascading triggers
  const newsletterUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingNewsletterContentRef = useRef<string | null>(null)
  const pendingNewsletterSectionsRef = useRef<any[] | null>(null)

  // Keep refs in sync with state (for when state updates from other sources)
  useEffect(() => {
    if (campaign.content) {
      contentRef.current = campaign.content
    }
  }, [campaign.content])

  useEffect(() => {
    if (campaign.content_data) {
      contentDataRef.current = campaign.content_data
    }
  }, [campaign.content_data])
  
  // Cleanup: Clear newsletter update timeout on unmount
  useEffect(() => {
    return () => {
      if (newsletterUpdateTimeoutRef.current) {
        clearTimeout(newsletterUpdateTimeoutRef.current)
      }
    }
  }, [])

  // Update "time ago" display with friendly text
  useEffect(() => {
    if (!lastSaved) {
      setTimeAgo("")
      return
    }

    const updateTimeAgo = () => {
      const now = new Date()
      const diff = Math.floor((now.getTime() - lastSaved.getTime()) / 1000) // seconds

      if (diff < 10) {
        setTimeAgo("a moment ago")
      } else if (diff < 60) {
        setTimeAgo("a few seconds ago")
      } else if (diff < 120) {
        setTimeAgo("a minute ago")
      } else if (diff < 300) {
        setTimeAgo("a few minutes ago")
      } else if (diff < 3600) {
        const minutes = Math.floor(diff / 60)
        setTimeAgo(`${minutes} minutes ago`)
      } else if (diff < 7200) {
        setTimeAgo("an hour ago")
      } else {
        const hours = Math.floor(diff / 3600)
        setTimeAgo(`${hours} hours ago`)
      }
    }

    // Update immediately
    updateTimeAgo()

    // Update every 5 seconds (no need for exact second precision)
    const interval = setInterval(updateTimeAgo, 5000)

    return () => clearInterval(interval)
  }, [lastSaved])

  // Track scroll position for sticky header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Load existing campaign data if in edit mode
  useEffect(() => {
    if (existingCampaign) {
      setCampaign({
        subject: existingCampaign.subject || "",
        content: existingCampaign.content || "",
        layout: existingCampaign.layout,
        target_selection: existingCampaign.target_selection || { recipients: [] },
        // Re-hydrate the campaign's SAVED attachments so they show on edit (they
        // were invisible before). These already live on the server, so they're
        // not re-uploaded on save; removing one deletes it server-side.
        files: savedAttachmentsToFiles((existingCampaign as any).attachments),
        content_data: (existingCampaign as any).content_data || null,
        from_address: existingCampaign.from_address || DEFAULT_CAMPAIGN_FROM,
        from_name: existingCampaign.from_name || "",
        reply_to: existingCampaign.reply_to || "",
      })
      setDraftCampaignId(existingCampaign.id)
      draftIdRef.current = existingCampaign.id

      // Initialize last saved refs to prevent immediate auto-save
      lastAutoSavedContentRef.current = existingCampaign.content || ""
      lastAutoSavedSubjectRef.current = existingCampaign.subject || ""
      lastAutoSavedSenderRef.current = JSON.stringify({
        from_address: existingCampaign.from_address || DEFAULT_CAMPAIGN_FROM,
        from_name: existingCampaign.from_name || "",
        reply_to: existingCampaign.reply_to || "",
      })
      contentRef.current = existingCampaign.content || ""
      contentDataRef.current = (existingCampaign as any).content_data || null

      // User is editing existing campaign, so they've made changes (from past sessions)
      hasUserMadeChangesRef.current = true
      autoSaveEnabledRef.current = true // Enable auto-save for existing campaigns
    }
    // Removed auto-selection: user must choose template first
  }, [existingCampaign])
  
  // Initialize lastAutoSaved refs when initial template loads (not from user changes)
  useEffect(() => {
    // Only initialize if we haven't tracked user changes yet (initial load)
    if (!hasUserMadeChangesRef.current) {
      // Initialize subject ref if we have a subject but ref is empty
      if (!lastAutoSavedSubjectRef.current && campaign.subject) {
        lastAutoSavedSubjectRef.current = campaign.subject
      }
      // Initialize target selection ref if empty
      if (!lastAutoSavedTargetSelectionRef.current) {
        lastAutoSavedTargetSelectionRef.current = JSON.stringify(campaign.target_selection)
      }
    }
  }, [campaign.subject, campaign.target_selection])

  // Handle layout selection - inject tenant information into template
  const handleLayoutChange = async (layoutId: EmailLayoutType) => {
    const layout = getLayoutById(layoutId)
    
    // Get tenant info from auth state - include ALL fields
    const tenantInfo: TenantInfo | undefined = tenant ? {
      name: tenant.name,
      logo_url: tenant.logo_url,
      company_name: tenant.company_name,
      address: tenant.address,
      city: tenant.city,
      state: tenant.state,
      postal_code: tenant.postal_code,
      country: tenant.country,
      phone: tenant.phone,
      email: tenant.email,
      website: tenant.website
    } : undefined
    
    // Inject tenant information into the template subject
    const subjectWithTenantInfo = injectTenantInfo(layout.defaultSubject, tenantInfo)

    // Template selection only sets local state. We deliberately do NOT create a
    // campaign row here anymore: the debounced auto-save is the *single* owner
    // of draft creation (guarded by `ensureCampaign`/`creatingDraftRef`), and it
    // swaps the URL to /edit/:id via history.replaceState once the row exists.
    // The old eager-create raced the auto-save into TWO rows on a slow network,
    // and littered the list with a "Draft - <timestamp>" row on every template
    // click even if the user abandoned it. One clean draft is now created ~1.5s
    // after the builder fills in content.
    if (layoutId === 'olive_green_newsletter' || layoutId === 'webinar' || layoutId === 'case_study') {
      // Section-built templates: don't set content here — the builder generates
      // the HTML and calls onContentChange. (Setting it here would fight the
      // builder and risk a "Content can't be blank" save before it mounts.)
      setCampaign({
        ...campaign,
        layout: layoutId,
        subject: subjectWithTenantInfo,
      })
    } else {
      // Simple-text and other plain templates ship their default content.
      const contentWithTenantInfo = injectTenantInfo(layout.defaultContent, tenantInfo)
      setCampaign({
        ...campaign,
        layout: layoutId,
        subject: subjectWithTenantInfo,
        content: contentWithTenantInfo,
      })
    }
  }

  // Helper function to check if content has meaningful data
  const hasValidContent = useCallback((content: string) => {
    if (!content) return false
    // Remove HTML tags and check if there's actual text content
    const textContent = content.replace(/<[^>]*>/g, '').trim()
    // Must have at least 3 characters of actual content
    return textContent.length >= 3
  }, [])
  
  // Debounced newsletter update handler to prevent cascading updates
  const handleNewsletterUpdate = useCallback(() => {
    const content = pendingNewsletterContentRef.current
    const sections = pendingNewsletterSectionsRef.current
    
    // Only update if we have pending changes
    if (content !== null || sections !== null) {
      setCampaign(prev => ({
        ...prev,
        ...(content !== null && { content }),
        ...(sections !== null && { content_data: { sections } })
      }))
      
      // Clear pending updates
      pendingNewsletterContentRef.current = null
      pendingNewsletterSectionsRef.current = null
    }
  }, [])

  // Single, race-safe owner of "make sure this campaign exists, then persist".
  // All save paths (auto-save, manual Save, back-nav) go through this ONE saver
  // so concurrent writers share a single create — see makeCampaignSaver for the
  // "creating one campaign makes two" bug it fixes. Created once (the deps it
  // closes over are all stable: the hook actions are useCallback'd, draftIdRef
  // is a ref, setDraftCampaignId is a stable setter).
  const saverRef = useRef<CampaignSaver | null>(null)
  if (!saverRef.current) {
    saverRef.current = makeCampaignSaver({
      getExistingId: () => (isEditMode && existingCampaign?.id) || draftIdRef.current,
      createCampaign: (data) => createCampaign(data as any),
      updateCampaign: (id, data) => updateCampaign(id, data as any),
      onCreated: (id) => {
        draftIdRef.current = id
        setDraftCampaignId(id)
        // Marketing ▸ Webinars: report the freshly-created campaign back to the
        // webinar that opened this editor, so its step 1 shows as done and can
        // deep-link here. Fail-soft (the helper never throws) — this is
        // bookkeeping, and losing it must not disturb the operator's editing.
        const webinarSeed = webinarSeedRef.current
        if (webinarSeed) {
          void linkCampaignToWebinar(webinarSeed.webinar_id, Number(id))
        }
        // Swap the URL to the edit route WITHOUT a remount so a refresh keeps
        // the draft. (Replaces the old navigate(), which remounted the form.)
        window.history.replaceState(null, '', emailCampaignsPath(`/edit/${id}`))
      },
    })
  }
  const ensureCampaign = useCallback(
    (fields: {
      subject: string
      content: string
      layout: EmailLayoutType | undefined
      target_selection: TargetSelection
      content_data: any
      from_address?: string
      from_name?: string
      reply_to?: string
    }) => saverRef.current!.ensure(fields),
    [],
  )

  // Auto-save function - saves draft in background
  const autoSaveDraft = useCallback(async () => {
    // STRICT CHECK: Auto-save must be explicitly enabled
    if (!autoSaveEnabledRef.current) {
      return
    }
    
    // Don't auto-save if user hasn't made any manual changes yet
    if (!hasUserMadeChangesRef.current) {
      return
    }
    
    // Don't auto-save if we're currently saving the final campaign
    if (isSaving) return
    
    // Use ref for content to get the latest value synchronously
    const currentContent = contentRef.current || campaign.content
    
    // Don't auto-save if content is empty or has no meaningful text
    if (!hasValidContent(currentContent)) {
      return
    }
    
    // Don't auto-save if subject is completely empty
    if (!campaign.subject || campaign.subject.trim().length === 0) {
      return
    }

    // Don't persist a known-invalid sender (crm-api would 422). The inline
    // error on the field already tells the user to fix it; once fixed, the next
    // edit re-attempts the save.
    if (validateFromAddress(campaign.from_address) || validateReplyTo(campaign.reply_to)) {
      return
    }

    // Check if anything has actually changed since last auto-save
    const contentChanged = currentContent !== lastAutoSavedContentRef.current
    const subjectChanged = campaign.subject !== lastAutoSavedSubjectRef.current
    const targetSelectionChanged = JSON.stringify(campaign.target_selection) !== lastAutoSavedTargetSelectionRef.current
    const filesChanged = campaign.files.length !== lastAutoSavedFilesRef.current
    const senderSignature = JSON.stringify({
      from_address: campaign.from_address,
      from_name: campaign.from_name,
      reply_to: campaign.reply_to,
    })
    const senderChanged = senderSignature !== lastAutoSavedSenderRef.current

    if (!contentChanged && !subjectChanged && !targetSelectionChanged && !filesChanged && !senderChanged) {
      return // No changes, skip silently
    }
    
    // Sanitize target selection for backend (convert 'contact' type to 'prospect')
    const sanitizedTargetSelection = sanitizeTargetSelection(campaign.target_selection)
    
    try {
      setIsAutoSaving(true)
      // One guarded owner of create-or-update — no second row, ever.
      const campaignId = await ensureCampaign({
        subject: campaign.subject,
        content: currentContent,
        layout: campaign.layout,
        target_selection: sanitizedTargetSelection,
        content_data: contentDataRef.current,
        from_address: campaign.from_address,
        from_name: campaign.from_name,
        reply_to: campaign.reply_to,
      })

      // Upload any pending local / file-manager files to the (now-existing) row.
      if (campaign.files && campaign.files.length > 0) {
        const filesToProcess = campaign.files.filter(file =>
          String(file.id).startsWith('local-') || file.source === 'file_manager'
        )
        if (filesToProcess.length > 0) {
          await uploadLocalFilesToCampaign(filesToProcess, campaignId)
        }
      }

      // Update last saved refs to prevent saving same content again
      lastAutoSavedContentRef.current = currentContent
      lastAutoSavedSubjectRef.current = campaign.subject
      lastAutoSavedTargetSelectionRef.current = JSON.stringify(campaign.target_selection)
      lastAutoSavedFilesRef.current = campaign.files.length
      lastAutoSavedSenderRef.current = senderSignature
      setLastSaved(new Date())
    } catch (error: any) {
      console.error('❌ Auto-save failed:', error)
      // Transient network blips stay silent (auto-save retries on the next
      // edit), but a SERVER VALIDATION error means the draft — including the
      // chosen recipients — was rejected and NOT saved. Surfacing it prevents
      // the silent data-loss that made selected recipients vanish (editor
      // showed them, list/Send Now saw none).
      const msg = error?.message || ''
      if (/recipient|target selection|missing required|invalid email/i.test(msg)) {
        toast({
          title: "Recipients not saved",
          description: msg,
          variant: "destructive",
        })
      }
    } finally {
      setIsAutoSaving(false)
    }
  }, [campaign.subject, campaign.content, campaign.target_selection, campaign.files, campaign.layout, campaign.content_data, campaign.from_address, campaign.from_name, campaign.reply_to, isSaving, ensureCampaign, hasValidContent, toast])

  // Signature of the WHOLE target selection (relationships, groups, industries,
  // exclusions AND individual recipients). The debounced auto-save effect keys
  // on this instead of `recipients.length` alone — a relationship/group/industry
  // audience (e.g. "All contacts") lives in `filters`/`groups`, so keying on the
  // individual-recipients length NEVER re-armed the timer for a pure-audience
  // change and the "Saved" state silently lagged the selection. Manual Save and
  // back-nav still persist it, but this makes auto-save catch audience-only edits.
  const targetSelectionSignature = JSON.stringify(campaign.target_selection)

  // Debounced auto-save effect - triggers 3 seconds after user stops typing
  // Only depends on specific primitive fields to avoid infinite loops
  useEffect(() => {
    renderCountRef.current += 1
    
    // Skip first render only (initial mount)
    // After that, any change to subject/content/recipients should trigger auto-save
    if (renderCountRef.current <= 1) {
      return
    }
    
    // Also skip if we don't have a subject yet (template still loading)
    if (!campaign.subject || campaign.subject.trim().length === 0) {
      return
    }
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }
    
    // Set new timeout for auto-save (1.5 seconds after last change - faster to catch quick edits)
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveDraft()
    }, 1500)
    
    // Cleanup timeout on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.subject, campaign.content, targetSelectionSignature, campaign.files.length, campaign.from_address, campaign.from_name, campaign.reply_to])

  // Handle save campaign (create or update)
  const handleSaveCampaign = async () => {
    // Use ref for content to get the latest value synchronously
    const finalContent = contentRef.current || campaign.content
    
    // Validate subject
    if (!campaign.subject || campaign.subject.trim().length === 0) {
      toast({
        title: "Subject Required",
        description: "Please enter an email subject before saving.",
        variant: "destructive",
      })
      return
    }
    
    // Sanitize target selection for backend (convert 'contact' type to 'prospect')
    const sanitizedTargetSelection = sanitizeTargetSelection(campaign.target_selection)
    
    try {
      setIsSaving(true)

      // Same single guarded owner as auto-save — create-or-update exactly once.
      const campaignId = await ensureCampaign({
        subject: campaign.subject,
        content: finalContent,
        layout: campaign.layout,
        target_selection: sanitizedTargetSelection,
        content_data: contentDataRef.current,
        from_address: campaign.from_address,
        from_name: campaign.from_name,
        reply_to: campaign.reply_to,
      })

      // Upload any pending files to the row.
      if (campaign.files && campaign.files.length > 0) {
        const filesToProcess = campaign.files.filter(file =>
          String(file.id).startsWith('local-') || file.source === 'file_manager'
        )
        if (filesToProcess.length > 0) {
          await uploadLocalFilesToCampaign(filesToProcess, campaignId)
        }
      }

      toast({
        title: "Success",
        description: isEditMode
          ? "Email campaign updated successfully"
          : "Email campaign created successfully",
      })

      // Navigate back to campaigns list
      navigate(emailCampaignsPath())

    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || `Failed to ${isEditMode ? 'update' : 'create'} email campaign`,
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Handle back navigation - save draft before leaving
  const handleBackClick = async () => {
    const currentContent = contentRef.current || campaign.content
    const hasContent = hasValidContent(currentContent)

    // Save for BOTH edit mode and create mode if there are changes
    if (autoSaveEnabledRef.current && campaign.subject && hasContent) {
      // Sanitize target selection for backend (convert 'contact' type to 'prospect')
      const sanitizedTargetSelection = sanitizeTargetSelection(campaign.target_selection)

      try {
        // Check if content has changed from last auto-save
        const contentChanged = currentContent !== lastAutoSavedContentRef.current
        const subjectChanged = campaign.subject !== lastAutoSavedSubjectRef.current
        const targetChanged = JSON.stringify(campaign.target_selection) !== lastAutoSavedTargetSelectionRef.current
        const senderChanged = JSON.stringify({
          from_address: campaign.from_address,
          from_name: campaign.from_name,
          reply_to: campaign.reply_to,
        }) !== lastAutoSavedSenderRef.current

        // Only save if something actually changed — through the one guarded
        // owner, so leaving the page can't spawn a duplicate either.
        if (contentChanged || subjectChanged || targetChanged || senderChanged) {
          await ensureCampaign({
            subject: campaign.subject,
            content: currentContent,
            layout: campaign.layout,
            target_selection: sanitizedTargetSelection,
            content_data: contentDataRef.current,
            from_address: campaign.from_address,
            from_name: campaign.from_name,
            reply_to: campaign.reply_to,
          })
        }
      } catch (error) {
        console.error('❌ Failed to save before navigation:', error)
      }
    }

    // Navigate back
    navigate(emailCampaignsPath())
  }

  // Handle send test email
  const handleSendTestEmail = async (email: string) => {
    // Use ref for content to get the latest value synchronously
    const finalContent = contentRef.current || campaign.content

    // Block an invalid sender before a needless round-trip (crm-api would 422).
    // The dialog surfaces the thrown message inline.
    const senderError = validateFromAddress(campaign.from_address) || validateReplyTo(campaign.reply_to)
    if (senderError) {
      toast({ title: "Invalid sender", description: senderError, variant: "destructive" })
      throw new Error(senderError)
    }

    // Per-layout pre-flight (e.g. the masthead template requires a registration
    // link — its always-on "Register Now" buttons need a destination).
    const layoutError = campaignSendBlockReason({ layout: campaign.layout, content_data: contentDataRef.current })
    if (layoutError) {
      toast({ title: "Can't send yet", description: layoutError, variant: "destructive" })
      throw new Error(layoutError)
    }

    try {
      let attachment_blob_ids: string[] = []

      // If we have files, upload them to a temporary campaign for testing
      if (campaign.files.length > 0) {
        // Empty recipients array is fine for test campaigns - no need to sanitize
        const tempCampaign = await createCampaign({
          name: `Test Email (Temporary - ${Date.now()})`,
          subject: campaign.subject,
          content: finalContent,
          layout: campaign.layout,
          status: 'not_sent',
          target_selection: { recipients: [] },
          from_address: campaign.from_address,
          from_name: campaign.from_name,
          reply_to: campaign.reply_to,
        })
        
        try {
          const uploadedFiles = await uploadLocalFilesToCampaign(campaign.files, tempCampaign.campaign.id)
          attachment_blob_ids = uploadedFiles
            .filter(file => file.blob_key)
            .map(file => file.blob_key!)

          // The temp campaign only has the freshly-uploaded local files. Tell
          // the backend to also copy the campaign's SAVED attachments (still in
          // the editor) so the test send includes them too.
          const sourceCampaignId = existingCampaign?.id ?? draftCampaignId ?? undefined
          const keptSavedAttachmentIds = campaign.files
            .filter(f => f.blob_key && !String(f.id).startsWith('local-') && f.source !== 'file_manager')
            .map(f => f.id)

          await sendTestEmail({
            email,
            recipient_name: user?.full_name || '',
            subject: campaign.subject,
            content: finalContent,
            layout: campaign.layout,
            campaign_id: tempCampaign.campaign.id,
            ...(sourceCampaignId && keptSavedAttachmentIds.length > 0
              ? { source_campaign_id: sourceCampaignId, keep_attachment_ids: keptSavedAttachmentIds }
              : {}),
          })
          
          await deleteCampaign(tempCampaign.campaign.id)
        } catch (uploadError) {
          await deleteCampaign(tempCampaign.campaign.id).catch(() => {})
          throw uploadError
        }
      } else {
        // No files, just send test email
        await sendTestEmail({
          email,
          recipient_name: user?.full_name || '',
          subject: campaign.subject,
          content: finalContent,
          layout: campaign.layout,
          attachment_blob_ids: [],
          from_address: campaign.from_address,
          from_name: campaign.from_name,
          reply_to: campaign.reply_to,
        })
      }
      
      toast({
        title: "Test Email Sent",
        description: `A test email has been sent to ${email}`,
      })
    } catch (error: any) {
      // Surface the server's real reason (crm-api returns `{ errors: [..] }`)
      // rather than axios's opaque "Request failed with status code 422".
      const message = extractApiErrorMessage(error, "Failed to send test email")
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      })
      // Re-throw a clean Error so the dialog's inline error shows the same
      // human-readable reason (it reads `err.message`).
      throw new Error(message)
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <FormHeader
        isEditMode={isEditMode}
        isScrolled={isScrolled}
        isSaving={isSaving}
        onBackClick={handleBackClick}
        onSave={handleSaveCampaign}
        canSave={!!campaign.layout}
        onSendTest={() => setShowTestEmailDialog(true)}
      />

      {/* Main Content — template-first; once a template is picked: a section dot-
          rail (left), the From/To/Body/Attachments form (middle), and an always-
          visible sticky live preview (right). */}
      <Box
        sx={{
          mt: 1.5,
          display: "grid",
          gap: 3,
          alignItems: "start",
          gridTemplateColumns: campaign.layout
            ? { xs: "1fr", lg: "max-content minmax(0, 1.1fr) minmax(0, 0.85fr)" }
            : "1fr",
        }}
      >
        {/* Section dot-nav (sticky, lg+ only) */}
        {campaign.layout && <SectionNav />}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            minWidth: 0,
            width: "100%",
            maxWidth: campaign.layout ? "none" : 920,
            mx: campaign.layout ? 0 : "auto",
          }}
        >
          <ContentEditor
            campaign={campaign}
            setCampaign={setCampaign}
            tenant={tenant}
            existingCampaignId={existingCampaign?.id ?? draftCampaignId ?? undefined}
            isAutoSaving={isAutoSaving}
            lastSaved={lastSaved}
            timeAgo={timeAgo}
            onLayoutChange={handleLayoutChange}
            onNewsletterUpdate={handleNewsletterUpdate}
            toast={toast}
            recipientsSlot={
              <Card id={CAMPAIGN_SECTIONS[1]!.id} sx={{ scrollMarginTop: `${SECTION_SCROLL_MARGIN_TOP}px` }}>
                <CardHeader
                  title={
                    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
                      <Users size={20} />
                      To
                    </Box>
                  }
                  subheader="Select who will receive this email campaign"
                />
                <CardContent>
                  <AudienceBuilder
                    value={campaign.target_selection}
                    onChange={(selection) => {
                      autoSaveEnabledRef.current = true
                      hasUserMadeChangesRef.current = true
                      setCampaign({ ...campaign, target_selection: selection })
                    }}
                  />
                </CardContent>
              </Card>
            }
            autoSaveEnabledRef={autoSaveEnabledRef}
            hasUserMadeChangesRef={hasUserMadeChangesRef}
            lastAutoSavedContentRef={lastAutoSavedContentRef}
            lastNewsletterContentRef={lastNewsletterContentRef}
            lastNewsletterSectionsRef={lastNewsletterSectionsRef}
            contentRef={contentRef}
            contentDataRef={contentDataRef}
            pendingNewsletterContentRef={pendingNewsletterContentRef}
            pendingNewsletterSectionsRef={pendingNewsletterSectionsRef}
            newsletterUpdateTimeoutRef={newsletterUpdateTimeoutRef}
          />
        </Box>

        {/* Right column — always-visible, sticky live email preview (direct grid
            child so `position: sticky` sticks within the tall grid row). */}
        {campaign.layout && (
          <CampaignEmailPreview
            layout={campaign.layout}
            subject={campaign.subject}
            content={campaign.content}
            tenant={tenant}
          />
        )}
      </Box>

      {/* Send Test Email Dialog */}
      <SendTestEmailDialog
        open={showTestEmailDialog}
        onOpenChange={setShowTestEmailDialog}
        onSendTest={handleSendTestEmail}
        defaultEmail={user?.email || ""}
      />
    </Box>
  )
}


export default EmailCampaignForm
